import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle, Phone, Mail, Play, Users, Target, Leaf } from "lucide-react";

const HERO_IMG = "/local-assets/xcjhcq5g_CCR-I-2026-I-Camerali-I-Green-Business-I-Banner-Web-1.jpg";

const VIDEOS = [
  {
    id: "froKVFO3bSI",
    titolo: "Oltre il profitto: perché la sostenibilità",
    ospite: "Dialogo con Andrea Locatelli",
    durata: "20:15",
  },
  {
    id: "-y_a1q2qWC0",
    titolo: "Misurare l'impatto, creare valore — Persone e Innovazione",
    ospite: "Dialogo con Antonio Pelosi",
    durata: "22:03",
  },
  {
    id: "V6dc4ZJqHPE",
    titolo: "Comunità e competenze per un futuro sostenibile",
    ospite: "Andrea Fassi",
    durata: "21:11",
  },
];

const DESTINATARI = [
  "Imprese che lavorano come fornitori per grandi aziende obbligate al bilancio di sostenibilità",
  "PMI del commercio, artigianato, agroalimentare, servizi, turismo e manifattura",
  "Start-up e imprese innovative attive nel settore ambientale, energetico e green tech",
  "Aziende che vogliono rafforzare la propria reputazione e aprirsi a nuovi mercati",
  "Realtà imprenditoriali interessate ad allinearsi agli standard ESG e ai requisiti normativi europei",
];

const BENEFICI = [
  {
    icon: <Users size={22} className="text-emerald-600" />,
    titolo: "Workshop gratuiti",
    desc: "Online e in presenza, con esperti di sostenibilità aziendale.",
  },
  {
    icon: <Target size={22} className="text-emerald-600" />,
    titolo: "Consulenza personalizzata",
    desc: "Per l'adozione del bilancio di sostenibilità nella tua impresa.",
  },
  {
    icon: <Leaf size={22} className="text-emerald-600" />,
    titolo: "Piattaforma digitale",
    desc: "Accesso a guide pratiche, materiali e strumenti per misurare il tuo impatto.",
  },
];

// Ordine canonico dei ruoli partner nelle pagine progetto (da rispettare per ogni futura pagina):
// 1. "Un progetto di"  2. "Con il contributo di"  3. "Promosso da"  4. "Con il patrocinio di"  5. "In collaborazione con"
const PARTNER = [
  {
    ruolo: "Un progetto di",
    logo: "/local-assets/vcuc8i2s_Gruppo_partner_-_progetti_roma.png",
    nome: "Confcommercio Roma",
  },
  {
    ruolo: "Con il contributo di",
    logo: "https://www.confcommercioroma.it/wp-content/uploads/2024/01/camera-di-commercio-di-roma.png",
    nome: "Camera di Commercio di Roma",
  },
  {
    ruolo: "Promosso da",
    logo: "/local-assets/3201z59d_Gruppo_partner_-_progetti_giovani.png",
    nome: "CCR Giovani Imprenditori Roma",
  },
];

const EVENTI = [
  {
    giorno: "4",
    mese: "Feb",
    data: "4 Febbraio 2026",
    titolo: "Dalla teoria alla pratica: strumenti ESG per le PMI",
    path: "/eventi/dalla-teoria-alla-pratica-strumenti-esg-per-le-pmi",
    image: "/evento-esg.jpeg",
    tipo: "Workshop",
  },
  {
    giorno: "30",
    mese: "Set",
    data: "30 Settembre 2025",
    titolo: "Oltre il Profitto: innovazione sociale e sostenibilità nel futuro del lavoro",
    path: "/eventi/oltre-il-profitto-innovazione-sociale-e-sostenibilità-nel-futuro-del-lavoro",
    image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&q=80&auto=format",
    tipo: "Convegno",
  },
];

export default function GreenBusinessPage() {
  return (
    <main id="main-content" data-testid="green-business-page">

      {/* ── HERO ── */}
      <section className="relative h-[320px] md:h-[420px] overflow-hidden">
        <img
          src={HERO_IMG}
          alt="Green Business — bilancio di sostenibilità per le PMI"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4,30,66,0.92)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-12">
          <Link
            to="/progetti"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white mb-5 transition-colors w-fit"
            data-testid="back-to-progetti"
          >
            <ArrowLeft size={15} /> Tutti i progetti
          </Link>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed'] mb-2">
            Sostenibilità · In corso
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight max-w-3xl">
            Green Business
          </h1>
          <p className="mt-3 text-white/80 text-lg max-w-2xl">
            Il bilancio di sostenibilità per le PMI di Roma e Regione Lazio
          </p>
        </div>
      </section>

      {/* ── INTRO + SIDEBAR ── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">

            {/* Contenuto principale — 2/3 */}
            <div className="lg:col-span-2 space-y-12">

              {/* Intro */}
              <div>
                <p className="text-lg text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
                  Il progetto <strong className="text-[var(--confcom-text-primary)]">Green Business</strong> nasce per accompagnare le piccole e medie imprese di Roma e del Lazio verso un cambiamento concreto: il{" "}
                  <strong className="text-[var(--confcom-text-primary)]">bilancio di sostenibilità</strong>. Un'opportunità strategica per restare competitivi, rispondere alle richieste del mercato e prepararsi alle nuove normative europee, come il Green Deal e la Tassonomia UE.
                </p>
                <p className="text-[var(--confcom-text-secondary)] leading-relaxed">
                  Grazie a <strong className="text-[var(--confcom-text-primary)]">formazione, consulenza personalizzata e strumenti digitali</strong>, le imprese potranno approfondire pratiche sostenibili nella propria gestione e misurare il proprio impatto ambientale e sociale.
                </p>
              </div>

              {/* Il Progetto */}
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Il progetto</span>
                <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mt-2 mb-4">
                  Un percorso verso la sostenibilità concreta
                </h2>
                <p className="text-[var(--confcom-text-secondary)] leading-relaxed">
                  Green Business è promosso da <strong className="text-[var(--confcom-text-primary)]">Confcommercio Roma</strong>, con il contributo della{" "}
                  <strong className="text-[var(--confcom-text-primary)]">Camera di Commercio di Roma</strong> e partner strategici, ed è pensato per rendere la sostenibilità accessibile anche alle PMI. Attraverso una serie di{" "}
                  <strong className="text-[var(--confcom-text-primary)]">workshop formativi</strong>, incontri e contenuti digitali, il progetto guida le imprese nella comprensione del bilancio di sostenibilità, uno strumento sempre più richiesto da clienti, investitori e grandi aziende.
                </p>
              </div>

              {/* Gli Obiettivi */}
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Gli obiettivi</span>
                <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mt-2 mb-4">
                  Gestione aziendale responsabile
                </h2>
                <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
                  L'obiettivo generale è <strong className="text-[var(--confcom-text-primary)]">sostenere le PMI nel percorso verso una gestione aziendale più responsabile e sostenibile</strong>. Green Business vuole stimolare una cultura imprenditoriale attenta agli impatti ambientali e sociali, fornendo strumenti concreti per affrontare il cambiamento in atto.
                </p>
                <p className="text-[var(--confcom-text-secondary)] leading-relaxed">
                  Le PMI potranno così migliorare la propria trasparenza verso l'esterno, posizionarsi come imprese virtuose, accedere a finanziamenti dedicati alla transizione ecologica ed essere pronte a partecipare a bandi e opportunità di collaborazione che richiedono criteri ESG.
                </p>
              </div>

              {/* I Destinatari */}
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">I destinatari</span>
                <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mt-2 mb-5">
                  A chi è rivolto
                </h2>
                <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
                  Green Business si rivolge a tutte le <strong className="text-[var(--confcom-text-primary)]">PMI con sede a Roma o nella Regione Lazio</strong>, in particolare a:
                </p>
                <ul className="space-y-3">
                  {DESTINATARI.map((d, i) => (
                    <li key={i} className="flex items-start gap-3 text-[var(--confcom-text-secondary)]">
                      <CheckCircle size={18} className="text-[var(--confcom-blue)] shrink-0 mt-0.5" aria-hidden="true" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </div>

            </div>

            {/* Sidebar — 1/3 */}
            <div className="space-y-6">

              {/* Benefici */}
              <div className="bg-[#F0F9F4] border border-emerald-100 rounded-2xl p-6">
                <h3 className="text-sm font-bold uppercase tracking-widest text-emerald-700 mb-4">Benefici per le aziende</h3>
                <div className="space-y-4">
                  {BENEFICI.map((b, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center shrink-0 shadow-sm">
                        {b.icon}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[var(--confcom-text-primary)]">{b.titolo}</p>
                        <p className="text-xs text-[var(--confcom-text-secondary)] mt-0.5 leading-relaxed">{b.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contatti */}
              <div className="bg-white border border-[var(--confcom-border)] rounded-2xl p-6">
                <h3 className="text-base font-bold text-[var(--confcom-text-primary)] mb-5">Informazioni</h3>
                <div className="space-y-4">
                  <a href="tel:0668437353" className="flex items-center gap-3 text-sm text-[#2d4a7a] hover:text-[var(--confcom-blue)] transition-colors">
                    <Phone size={18} className="text-[var(--confcom-blue)] shrink-0" />
                    06.68437353 / 271
                  </a>
                  <a href="mailto:Associati@confcommercioroma.it" className="flex items-center gap-3 text-sm text-[#2d4a7a] hover:text-[var(--confcom-blue)] transition-colors break-all">
                    <Mail size={18} className="text-[var(--confcom-blue)] shrink-0" />
                    Associati@confcommercioroma.it
                  </a>
                </div>
              </div>

              {/* Partnership loghi */}
              <div className="bg-white border border-[var(--confcom-border)] rounded-2xl p-6">
                <h3 className="text-base font-bold text-[var(--confcom-text-primary)] mb-5">Partnership</h3>
                <div>
                  {PARTNER.map((p, i) => (
                    <div key={p.nome} className="mb-8">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--confcom-text-muted)] mb-4">{p.ruolo}</p>
                      <div className="h-14 flex items-center">
                        <img
                          src={p.logo}
                          alt={p.nome}
                          className="h-full max-w-[160px] object-contain object-left"
                          style={{ mixBlendMode: "multiply" }}
                          loading="lazy"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ── VIDEO — Dialoghi sulla Sostenibilità ── */}
      <section className="py-16 bg-[#041e42]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Playlist · 3 video</span>
          <h2 className="text-2xl md:text-3xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] mt-2 mb-10">
            Dialoghi sulla Sostenibilità
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {VIDEOS.map((v) => (
              <a
                key={v.id}
                href={`https://www.youtube.com/watch?v=${v.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group block rounded-xl overflow-hidden border border-white/10 hover:border-[#56B7E6]/50 transition-all"
                data-testid={`video-${v.id}`}
              >
                <div className="relative">
                  <img
                    src={`https://img.youtube.com/vi/${v.id}/maxresdefault.jpg`}
                    alt={v.titolo}
                    className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/20 transition-colors">
                    <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm border border-white/40 flex items-center justify-center group-hover:opacity-90 transition-opacity">
                      <Play size={20} className="text-white ml-1" fill="white" aria-hidden="true" />
                    </div>
                  </div>
                  <span className="absolute bottom-2 right-2 text-xs font-medium text-white bg-black/60 px-1.5 py-0.5 rounded">
                    {v.durata}
                  </span>
                </div>
                <div className="bg-[#0a2a5e] p-4">
                  <p className="text-sm font-semibold text-white leading-snug mb-1">{v.titolo}</p>
                  <p className="text-xs text-white/60">{v.ospite}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── EVENTI COLLEGATI ── */}
      <section className="py-14 bg-[#F8FAFD]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Calendario</span>
          <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mt-2 mb-8">
            Eventi collegati
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {EVENTI.map((e) => (
              <Link
                key={e.titolo}
                to={e.path}
                className="group bg-white rounded-xl border border-[var(--confcom-border)] overflow-hidden hover:shadow-lg transition-all"
              >
                <div className="h-44 overflow-hidden relative">
                  <img
                    src={e.image}
                    alt={e.titolo}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4 w-16 h-16 bg-[var(--confcom-blue)] rounded-xl flex flex-col items-center justify-center text-white shadow-lg">
                    <span className="text-2xl font-bold font-['Encode_Sans_Semi_Condensed'] leading-none">{e.giorno}</span>
                    <span className="text-xs uppercase font-medium">{e.mese}</span>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs font-medium text-[var(--confcom-blue)] bg-[var(--confcom-bg-blue-light)] px-2.5 py-1 rounded-md">{e.tipo}</span>
                    <span className="text-xs text-[var(--confcom-text-muted)]">{e.data}</span>
                  </div>
                  <h3 className="text-base font-semibold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] leading-snug group-hover:text-[var(--confcom-blue)] transition-colors mb-2">
                    {e.titolo}
                  </h3>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--confcom-blue)]">
                    Scopri di più <ArrowRight size={12} aria-hidden="true" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 bg-[var(--confcom-blue)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl font-bold text-white font-['Encode_Sans_Semi_Condensed']">Vuoi partecipare a Green Business?</h2>
            <p className="text-white/80 mt-1">Contattaci per ricevere tutte le informazioni sul progetto.</p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Link
              to="/contatti"
              className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity shadow"
            >
              Contattaci <ArrowRight size={16} aria-hidden="true" />
            </Link>
            <Link
              to="/progetti"
              className="inline-flex items-center gap-2 border-2 border-white/40 text-white px-7 py-3 rounded-md font-medium hover:opacity-90 transition-opacity"
            >
              <ArrowLeft size={16} aria-hidden="true" /> Tutti i progetti
            </Link>
          </div>
        </div>
      </section>

    </main>
  );
}
